import { Check, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { PRODUCTS } from "../../data/products";

export default function ProductSelector() {
  return (
    <section id="produkty" data-testid="products-section" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          overline="01 · Nasze produkty"
          title="Trzy klasy. Jedna jakość."
          subtitle="Wybierz odpowiednią wełnę mineralną szklaną BUDOBET.PL do swojego projektu."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-14 border-2 border-black">
          {PRODUCTS.map((p, idx) => (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className={`relative bg-white p-8 lg:p-10 flex flex-col group ${
                idx !== PRODUCTS.length - 1 ? "md:border-r-2 border-black" : ""
              } ${
                idx !== PRODUCTS.length - 1 ? "border-b-2 md:border-b-0 border-black" : ""
              } hover:bg-zinc-50 transition`}
            >
              {/* Badge */}
              <div className="flex items-center justify-between mb-6">
                <span
                  className={`font-tech text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 ${
                    p.id === "033"
                      ? "bg-red-600 text-white"
                      : p.id === "035"
                      ? "bg-black text-white"
                      : "bg-zinc-200 text-zinc-900"
                  }`}
                >
                  {p.badge}
                </span>
                <span className="font-tech text-xs text-zinc-500 uppercase tracking-widest">
                  λ W/m·K
                </span>
              </div>

              {/* Lambda value - HUGE */}
              <div className="mb-4">
                <div
                  className={`font-display text-7xl lg:text-8xl leading-none ${p.accentClass}`}
                >
                  {p.code}
                </div>
              </div>

              {/* Product image */}
              <div className="relative h-52 mb-6 bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className="h-full w-auto max-w-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <span className="absolute top-2 left-2 bg-white border border-black px-2 py-0.5 font-tech text-[10px] uppercase tracking-widest">
                  {p.name}
                </span>
              </div>

              <p className="text-zinc-700 text-sm leading-relaxed mb-6 flex-1">
                {p.description}
              </p>

              {/* Specs */}
              <ul className="space-y-2 mb-8 font-tech text-sm">
                <SpecItem label="Reakcja na ogień" value={`Klasa ${p.fireClass}`} />
                <SpecItem
                  label="Grubości"
                  value={`${p.variants.map((v) => v.thickness).join(" · ")} mm`}
                />
                <SpecItem label="Szerokość" value="1,20 m" />
                <SpecItem label="Hydrofobizowana" value="TAK" />
              </ul>

              <Button
                asChild
                className={`rounded-none w-full h-12 font-bold uppercase tracking-wider text-white ${
                  p.id === "033"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-black hover:bg-zinc-800"
                }`}
                data-testid={`product-cta-${p.id}`}
              >
                <a href="#kalkulator">
                  Oblicz zapotrzebowanie
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SpecItem({ label, value }) {
  return (
    <li className="flex items-center justify-between border-b border-zinc-100 pb-2">
      <span className="text-zinc-500 uppercase text-[11px] tracking-widest">
        {label}
      </span>
      <span className="text-zinc-900 font-semibold flex items-center gap-1.5">
        <Check className="h-3.5 w-3.5 text-red-600" /> {value}
      </span>
    </li>
  );
}

export function SectionHeading({ overline, title, subtitle, light = false }) {
  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-5">
        <span className={`h-[2px] w-10 ${light ? "bg-red-500" : "bg-red-600"}`} />
        <span
          className={`font-tech text-xs tracking-[0.3em] uppercase font-bold ${
            light ? "text-red-500" : "text-red-600"
          }`}
        >
          {overline}
        </span>
      </div>
      <h2
        className={`font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[1] tracking-tighter ${
          light ? "text-white" : "text-zinc-900"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`mt-5 text-lg leading-relaxed max-w-2xl ${
            light ? "text-zinc-300" : "text-zinc-600"
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
